import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { t } from '../../i18n';

/**
 * <cadence-reset-password-page> — token-based password reset for admins/teachers.
 * The single-use token arrives in the URL (`/reset-password?token=…`, emailed by
 * `services/transactional.ts`). Students reset via onboarding instead.
 */
@customElement('cadence-reset-password-page')
export class CadenceResetPasswordPage extends LitElement {
  @state() private password = '';
  @state() private confirm = '';
  @state() private loading = false;
  @state() private done = false;
  @state() private error = '';

  private readonly token = new URLSearchParams(window.location.search).get('token') ?? '';

  static styles = css`
    :host { display: grid; place-items: center; min-height: 100vh; padding: var(--space-4); }
    .card {
      width: 100%; max-width: 400px; background: var(--color-bg-primary);
      border: 1px solid var(--color-border); border-radius: var(--radius-lg);
      box-shadow: var(--shadow-md); padding: var(--space-6);
    }
    h2 { margin: 0 0 4px; font-size: 1.2rem; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); font-size: 0.9rem; }
    form { display: flex; flex-direction: column; gap: var(--space-4); }
    .back { display: inline-block; margin-top: var(--space-4); color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    .success { color: var(--color-success); font-size: 0.92rem; }
    .error { color: var(--color-danger); font-size: 0.88rem; margin: 0; }
  `;

  private async submit() {
    this.error = '';
    if (!this.token) { this.error = t('auth.reset.missingToken'); return; }
    if (this.password.length < 8) { this.error = t('auth.reset.tooShort'); return; }
    if (this.password !== this.confirm) { this.error = t('auth.reset.mismatch'); return; }
    this.loading = true;
    try {
      await api.post('/auth/reset-password', { token: this.token, password: this.password });
      this.done = true;
    } catch (e) {
      this.error = e instanceof ApiError && e.code === 'INVALID_TOKEN'
        ? t('auth.reset.invalidToken')
        : (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <div class="card">
        <h2>${t('auth.reset.title')}</h2>
        ${this.done
          ? html`
              <p class="success" role="status">${t('auth.reset.success')}</p>
              <a class="back" href="/login" data-link>${t('auth.reset.signin')}</a>`
          : !this.token
            ? html`
                <p class="error" role="alert">${t('auth.reset.missingToken')}</p>
                <a class="back" href="/forgot-password" data-link>${t('auth.forgot.title')}</a>`
            : html`
                <p class="sub">${t('auth.reset.subtitle')}</p>
                <form @submit=${(e: Event) => { e.preventDefault(); this.submit(); }}>
                  <cadence-input
                    label=${t('auth.reset.password')}
                    type="password"
                    autocomplete="new-password"
                    .value=${this.password}
                    @input=${(e: CustomEvent) => (this.password = e.detail)}
                  ></cadence-input>
                  <cadence-input
                    label=${t('auth.reset.confirm')}
                    type="password"
                    autocomplete="new-password"
                    .value=${this.confirm}
                    @input=${(e: CustomEvent) => (this.confirm = e.detail)}
                    @enter=${() => this.submit()}
                  ></cadence-input>
                  ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
                  <cadence-button type="submit" full-width ?loading=${this.loading} @click=${() => this.submit()}>
                    ${t('auth.reset.submit')}
                  </cadence-button>
                </form>
                <a class="back" href="/login" data-link>${t('auth.reset.back')}</a>`}
      </div>
    `;
  }
}
